const smoothing = 0;

const drawPath = (_points: any[]) => {
  // build the d attributes by looping over the points
  const d = _points.reduce((acc: any, point: any[], i: number, a: any) => {
    return i === 0
      ? `M ${point[0]},${point[1]}`
      : `${acc} ${bezierCommand(point, i, a)}`;
  }, "");
  return d;
};

interface PathSegment {
  pos: number;
  color: string;
}

function calculatePathSegments(
  points: [number, number, number][],
  windowSize: number
): PathSegment[] {
  const segments: PathSegment[] = [];
  const speeds: number[] = [];

  const numColors = 10;
  const colors = [
    "#00FF00",
    "#33FF00",
    "#66FF00",
    "#99FF00",
    "#CCFF00",
    "#FFFF00",
    "#FFCC00",
    "#FF9900",
    "#FF6600",
    "#FF3300",
    "#FF3300",
  ].reverse();

  for (let i = 0; i < points.length - 1; i++) {
    const startPoint = points[i];
    const endPoint = points[i + 1];
    const timeDiff = endPoint[2] - startPoint[2];
    const speed =
      timeDiff > 0
        ? Math.sqrt(
            Math.pow(endPoint[0] - startPoint[0], 2) +
              Math.pow(endPoint[1] - startPoint[1], 2)
          ) / timeDiff
        : 0;

    speeds.push(speed);

    if (speeds.length > windowSize) {
      speeds.shift();
    }

    const avgSpeed =
      speeds.reduce((acc, curr) => acc + curr, 0) / speeds.length;
    const speedInterval = Math.floor(avgSpeed * numColors);
    const colorIndex = Math.min(Math.max(speedInterval, 0), numColors - 1);
    const color = colors[colorIndex];

    segments.push({
      pos: i / (points.length - 1),
      color,
    });
  }

  return segments;
}

// Properties of a line
// I:  - pointA (array) [x,y]: coordinates
//     - pointB (array) [x,y]: coordinates
// O:  - (object) { length: l, angle: a }: properties of the line
const line = (pointA: number[], pointB: number[]) => {
  const lengthX = pointB[0] - pointA[0];
  const lengthY = pointB[1] - pointA[1];
  return {
    length: Math.sqrt(Math.pow(lengthX, 2) + Math.pow(lengthY, 2)),
    angle: Math.atan2(lengthY, lengthX),
  };
};

// Position of a control point
// I:  - current (array) [x, y]: current point coordinates
//     - previous (array) [x, y]: previous point coordinates
//     - next (array) [x, y]: next point coordinates
//     - reverse (boolean, optional): sets the direction
// O:  - (array) [x,y]: a tuple of coordinates
const controlPoint = (
  current: number[],
  previous: any,
  next: any,
  reverse: boolean
) => {
  // When 'current' is the first or last point of the array
  // 'previous' or 'next' don't exist.
  // Replace with 'current'
  const p = previous || current;
  const n = next || current;

  // Properties of the opposed-line
  const o = line(p, n);

  // If is end-control-point, add PI to the angle to go backward
  const angle = o.angle + (reverse ? Math.PI : 0);
  const length = o.length * smoothing;

  // The control point position is relative to the current point
  const x = current[0] + Math.cos(angle) * length;
  const y = current[1] + Math.sin(angle) * length;
  return [x, y];
};

const bezierCommand = (point: any[], i: number, a: any[]) => {
  // start control point
  const cps = controlPoint(a[i - 1], a[i - 2], point, false);

  // end control point
  const cpe = controlPoint(point, a[i - 1], a[i + 1], true);
  return `C ${cps[0]},${cps[1]} ${cpe[0]},${cpe[1]} ${point[0]},${point[1]}`;
};

export { calculatePathSegments, drawPath };
